import { decodeJwt } from '../../../decodeJwt';
import type { ParsedElevatedAccessToken } from '../elevatedAccessTokens.types';
import { normalizeScopes } from '../normalizeScopes';

/**
 * Validates and parses an elevated access token from a JWT string.
 * Extracts scopes, expiration, and validates the token structure.
 *
 * Policy decisions:
 * - Tokens without scopes are rejected (returns null)
 * - Tokens with empty scopes array are rejected (returns null)
 * - Tokens without exp field are rejected (returns null)
 * - Expired tokens are rejected (returns null)
 *
 * @param token - JWT string containing the elevated access token
 * @param now - Current date for expiration validation (defaults to new Date())
 * @returns Parsed token data or null if token is invalid/expired
 */
export const parseElevatedAccessToken = (
  token: string,
  now: Date = new Date()
): ParsedElevatedAccessToken | null => {
  const payload = decodeJwt(token);
  const rawScopeClaim = payload?.scope;
  const processedScopes = rawScopeClaim?.split(' ') ?? [];
  const filteredScopes = processedScopes.filter((s) => s.length > 0);
  const singleUse = payload.singleUse ?? false;

  // Reject tokens without scopes or with empty scopes
  if (!filteredScopes || filteredScopes.length === 0) {
    return null;
  }

  // Reject tokens without exp field
  if (!payload.exp) {
    return null;
  }

  // Convert exp (seconds) to Date (milliseconds)
  const expiresAt = new Date(payload.exp * 1000);

  // Reject expired tokens
  if (expiresAt <= now) {
    return null;
  }

  return {
    expiresAt,
    normalizedScopes: normalizeScopes(filteredScopes),
    scopes: filteredScopes,
    singleUse,
    token,
  };
};
